import { useTranslation } from 'react-i18next';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui';
import type { AssetBalanceDto } from '@shared/api/contracts';
import { WITHDRAWAL_NETWORKS, type WithdrawalNetwork } from '@shared/config/networks';
import { formatAmount } from '@shared/lib/format-amount';
import { getUsdtBalanceByNetwork } from '../../lib/network-balance';
import { getManualNetworkChainName } from '../../lib/usdt-network-meta';

interface WithdrawalNetworkFieldProps {
  network: string;
  assetBalances: AssetBalanceDto[];
  error?: string;
  disabled?: boolean;
  onChange: (network: WithdrawalNetwork) => void;
}

export function WithdrawalNetworkField({
  network,
  assetBalances,
  error,
  disabled,
  onChange,
}: WithdrawalNetworkFieldProps) {
  const { t } = useTranslation('cabinet');
  const chainName = network ? getManualNetworkChainName(network) : '';

  return (
    <div className="space-y-2">
      <Label id="withdrawal-network-label" htmlFor="withdrawal-network">
        {t('withdrawal.modal.network')}
      </Label>
      <Select
        value={network}
        onValueChange={(value) => onChange(value as WithdrawalNetwork)}
        disabled={disabled}
      >
        <SelectTrigger id="withdrawal-network" aria-labelledby="withdrawal-network-label">
          <SelectValue placeholder={t('withdrawal.modal.networkPlaceholder')}>
            {network ? (
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="truncate">{t('withdrawal.modal.networkOption', { network })}</span>
                <span className="truncate text-xs text-muted-foreground">{chainName}</span>
              </span>
            ) : null}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {WITHDRAWAL_NETWORKS.map((item) => {
            const balance = getUsdtBalanceByNetwork(assetBalances, item);
            return (
              <SelectItem
                key={item}
                value={item}
                className="items-start py-2 [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1"
              >
                <span className="flex w-full min-w-0 items-center gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">
                      {t('withdrawal.modal.networkOption', { network: item })}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {getManualNetworkChainName(item)}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {balance == null ? t('balance.amountUnknown') : `${formatAmount(balance)} USDT`}
                  </span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
